import {
  getDocs,
  collection,
  type Firestore,
  query,
  where,
  doc,
  setDoc,
  DocumentReference,
  orderBy,
  startAfter,
  limit,
  type QueryDocumentSnapshot,
  getCountFromServer,
  endAt,
  startAt
} from "firebase/firestore";
import { isTicket, type Ticket } from "../types";
import { logEvent, type Analytics } from "firebase/analytics";
import { LOCAL_STORAGE_KEYS } from "../constants";

export const PAGE_SIZE = 100;

export type TicketsPage = {
  items: Ticket[];
  lastCursor: QueryDocumentSnapshot | null; 
  hasNext: boolean; 
  total?: number;   
};

function baseQuery(database: Firestore, lottery: string) {
  return query(
    collection(database, "tickets"),
    where("isSold", "==", false),
    where(
      "distributor",
      "==",
      window.localStorage.getItem(LOCAL_STORAGE_KEYS.USER_DISTRIBUTOR_ID)
    ),
    where("lottery", "==", lottery),
    orderBy("numericIdentifier")
  );
}

export const getPaginatedAvailableTickets = async (
  database: Firestore,
  lottery: string,
  cursor: QueryDocumentSnapshot | null = null,
): Promise<TicketsPage> => {
  const base = baseQuery(database, lottery);
  const q = cursor
    ? query(base, startAfter(cursor), limit(PAGE_SIZE + 1))
    : query(base, limit(PAGE_SIZE + 1));

  const documents = await getDocs(q);

  const relevantDocuments = documents.docs.slice(0, PAGE_SIZE);
  const tickets: Ticket[] = [];
  relevantDocuments.forEach((relevantDocument) => {
    const data = relevantDocument.data();
    if (isTicket(data)) {
      tickets.push({ ...data, reference: relevantDocument.ref });
    } else {
      console.log(data, "is not a ticket");
    }
  });

  const hasNext = documents.docs.length > PAGE_SIZE;
  const lastCursor = relevantDocuments.length
    ? relevantDocuments[relevantDocuments.length - 1]
    : null;

  return { items: tickets, lastCursor, hasNext };
};

export const getAvailableTicketsCount = async (
  database: Firestore,
  lottery: string
): Promise<number> => {
  const agg = await getCountFromServer(baseQuery(database, lottery));
  return agg.data().count;
};

export const getSoldTickets = async (database: Firestore, lottery: string): Promise<Ticket[]> => {
  const availableTicketsQuery = query(
    collection(database, "tickets"),
    where("isSold", "==", true),
    where(
      "distributor",
      "==",
      window.localStorage.getItem(LOCAL_STORAGE_KEYS.USER_DISTRIBUTOR_ID)
    ),
    where("lottery", "==", lottery),
    orderBy("numericIdentifier")
  );
  const documents = await getDocs(availableTicketsQuery);
  const data: Ticket[] = [];
  documents.forEach((ticket) => {
    const ticketData = ticket.data();
    if (isTicket(ticketData)) {
      data.push(ticketData);
    }
  });
  return data;
};

export const searchTicketByIdentifier = async (
  database: Firestore,
  identifier: string
): Promise<Ticket[]> => {
  const availableTicketsQuery = query(
    collection(database, "tickets"),
    where(
      "distributor",
      "==",
      window.localStorage.getItem(LOCAL_STORAGE_KEYS.USER_DISTRIBUTOR_ID)
    ),
    where("isSold", "==", false),
    where("identifier", "==", `#${identifier}`),
    orderBy("identifier")
  );
  const documents = await getDocs(availableTicketsQuery);
  const data: Ticket[] = [];
  documents.forEach((ticket) => {
    const ticketData = ticket.data();
    if (isTicket(ticketData)) {
      data.push({ ...ticketData, reference: ticket.ref });
    }
  });
  return data;
};

export const markTicketsAsSold = async (
  database: Firestore,
  analytics: Analytics,
  ticketReferences: DocumentReference[]
): Promise<void> => {
  logEvent(analytics, "purchase", { numberOfTickets: ticketReferences.length });

  const BATCH_SIZE = 30;
  const ticketIds = ticketReferences.map((ref) => ref.id);
  const allDocs: import("firebase/firestore").QueryDocumentSnapshot[] = [];

  for (let i = 0; i < ticketIds.length; i += BATCH_SIZE) {
    const chunk = ticketIds.slice(i, i + BATCH_SIZE);
    const q = query(collection(database, "tickets"), where("uid", "in", chunk));
    const snap = await getDocs(q);
    allDocs.push(...snap.docs);
  }

  let areAllTicketsAvailable = true;
  const soldTickets: string[] = [];

  allDocs.forEach((availableDoc) => {
    const currentTicket = availableDoc.data();
    if (currentTicket.isSold) {
      soldTickets.push(currentTicket.identifier as string);
    }
    areAllTicketsAvailable = areAllTicketsAvailable && !currentTicket.isSold;
  });

  if (areAllTicketsAvailable) {
    ticketReferences.forEach((ticketReference) => {
      const ticketRef = doc(database, "tickets", ticketReference.id);
      setDoc(
        ticketRef,
        {
          isSold: true,
          soldDate: new Date(),
          soldBy: localStorage.getItem(LOCAL_STORAGE_KEYS.USER_NAME),
        },
        { merge: true }
      );
    });
  } else {
    throw new Error(`Tickets ${soldTickets.join(", ")} are already sold`);
  }
};

export const searchTicketsByNumberWindow = async (
  database: Firestore,
  lottery: string,
  raw: string,
  windowSize: number = 100
): Promise<Ticket[]> => {
  const digits = (raw || "").replace(/\D+/g, "");
  if (!digits) return [];

  const n = parseInt(digits, 10);
  if (Number.isNaN(n)) return [];

  const lower = Math.floor(n / windowSize) * windowSize;
  const upper = lower + windowSize;

  const base = baseQuery(database, lottery);

  const q = query(base, startAt(lower), endAt(upper), limit(PAGE_SIZE));

  const snap = await getDocs(q);

  const items: Ticket[] = [];
  snap.forEach((docSnap) => {
    const data = docSnap.data();
    if (isTicket(data)) {
      items.push({ ...data, reference: docSnap.ref });
    }
  });

  return items;
};
